import {useEffect, useState} from "react";
import {Empty, Table, Tag, Tooltip} from "antd";
import {Button, FontAwesomeIcon} from "~/components";

/**
 * Bảng từ khoá trending dùng chung cho tab "Xu hướng hôm nay" và tab "Lịch sử".
 * Chọn nhiều dòng → nút "Lưu N từ khoá vào kho" (parent mở modal chọn dự án);
 * expandable = danh sách tin tức liên quan của từ khoá.
 *
 * Props tuỳ chọn: onSuggest(row) = nút "Gợi ý bài viết" (AI bắt trend);
 * onShowHistory(row) = bấm badge "X ngày" mở biểu đồ tần suất trend.
 */
function TrendsTable({items = [], loading = false, showDays = true, onSave, onSuggest, onShowHistory}) {

	const [selectedKeys, setSelectedKeys] = useState([]);

	// Đổi geo/ngày → dữ liệu mới, bỏ chọn cũ (hash cũ không còn trong bảng).
	// Trả lại prev khi đã rỗng để bail-out — items có thể là mảng mới mỗi render lúc loading.
	useEffect(() => {
		setSelectedKeys((prev) => (prev.length ? [] : prev));
	}, [items]);

	const selectedRows = items.filter((it) => selectedKeys.includes(it.hash));

	const columns = [
		{
			title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword', ellipsis: true,
			render: (v, r) => (
				<span>
					<strong>{v}</strong>
					{r.source_label && (
						<Tooltip title={`Từ khoá thịnh hành ở ${r.source_label}`}>
							<Tag color="geekblue" style={{marginLeft: 8}}>
								<FontAwesomeIcon icon="fa-light fa-location-dot" /> {r.source_label}
							</Tag>
						</Tooltip>
					)}
					{showDays && r.days_trended > 1 && (
						<Tooltip title={`Xuất hiện trong bảng trending ${r.days_trended} ngày (30 ngày gần nhất)${onShowHistory ? ' — bấm để xem biểu đồ' : ''}`}>
							<Tag
								color="volcano"
								style={{marginLeft: 8, ...(onShowHistory ? {cursor: 'pointer'} : {})}}
								onClick={onShowHistory ? () => onShowHistory(r) : undefined}
							>
								<FontAwesomeIcon icon="fa-light fa-fire" /> {r.days_trended} ngày
							</Tag>
						</Tooltip>
					)}
				</span>
			),
		},
		{
			// Không đặt defaultSortOrder — giữ nguyên thứ hạng của Google (độ mới + đột biến);
			// user vẫn tự bấm sort theo lượt tìm kiếm được.
			title: 'Lượt tìm kiếm', dataIndex: 'approx_traffic', key: 'traffic', align: 'center', width: 140,
			sorter: (a, b) => (a.traffic_min || 0) - (b.traffic_min || 0),
			render: (v) => v ? <Tag color="blue">{v}</Tag> : <span style={{color: '#9ca3af'}}>—</span>,
		},
		{
			// Chỉ nguồn batch (giống trang Google) mới có % tăng trưởng; RSS fallback = 0 → "—".
			title: 'Tăng trưởng', dataIndex: 'growth_pct', key: 'growth', align: 'center', width: 110, responsive: ['md'],
			sorter: (a, b) => (a.growth_pct || 0) - (b.growth_pct || 0),
			render: (v) => v > 0
				? <Tag color="volcano"><FontAwesomeIcon icon="fa-light fa-arrow-trend-up" /> {v >= 1000 ? '1000%+' : `+${v}%`}</Tag>
				: <span style={{color: '#9ca3af'}}>—</span>,
		},
		{
			title: 'Thời điểm', dataIndex: 'pub_date', key: 'pub_date', align: 'center', width: 150, responsive: ['md'],
			render: (v) => v || <span style={{color: '#9ca3af'}}>—</span>,
		},
		{
			// Nguồn batch không có tin (title/url) inline nhưng có truy vấn liên quan; RSS thì ngược lại.
			title: 'Liên quan', key: 'news', align: 'center', width: 110, responsive: ['md'],
			render: (_, r) => (r.news?.length
				? <span>{r.news.length} tin</span>
				: r.related?.length
					? <span>{r.related.length} truy vấn</span>
					: <span style={{color: '#9ca3af'}}>—</span>),
		},
		{
			title: '', key: 'action', align: 'right', width: onSuggest ? 110 : 60,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 6}}>
					{onSuggest && (
						<Tooltip title="AI gợi ý góc bài viết bắt trend">
							<Button small white onClick={() => onSuggest(r)}
								leftIcon={<FontAwesomeIcon icon="fa-light fa-wand-magic-sparkles" />} />
						</Tooltip>
					)}
					<Tooltip title="Lưu từ khoá này vào kho">
						<Button small white onClick={() => onSave?.([r])}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />} />
					</Tooltip>
				</span>
			),
		},
	];

	if (!loading && items.length === 0) {
		return <Empty description="Không có dữ liệu xu hướng." />;
	}

	return (
		<div>
			<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap'}}>
				<Button primary small disabled={selectedRows.length === 0} onClick={() => onSave?.(selectedRows)}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-bookmark" />}>
					Lưu {selectedRows.length > 0 ? `${selectedRows.length} ` : ''}từ khoá vào kho
				</Button>
				<span style={{color: '#6b7280', fontSize: 13}}>{items.length} từ khoá trending</span>
			</div>

			<Table
				rowKey="hash"
				size="small"
				loading={loading}
				columns={columns}
				dataSource={items}
				scroll={{x: 'max-content'}}
				pagination={{pageSize: 25, hideOnSinglePage: true, showSizeChanger: false}}
				rowSelection={{
					selectedRowKeys: selectedKeys,
					onChange: (keys) => setSelectedKeys(keys),
				}}
				expandable={{
					rowExpandable: (r) => (r.news?.length || 0) > 0 || (r.related?.length || 0) > 0,
					expandedRowRender: (r) => (r.news?.length
						? (
							<ul style={{margin: 0, paddingLeft: 18, display: 'grid', gap: 6}}>
								{r.news.map((n, i) => (
									<li key={i}>
										<a href={n.url} target="_blank" rel="noopener noreferrer">{n.title}</a>
										{n.source && <span style={{color: '#6b7280', marginLeft: 8}}>— {n.source}</span>}
									</li>
								))}
							</ul>
						)
						: (
							// Nguồn batch: truy vấn con mà người dùng cùng tìm với từ khoá trending.
							<div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
								{r.related.map((q, i) => <Tag key={i}>{q}</Tag>)}
							</div>
						)),
				}}
			/>
		</div>
	);
}

export default TrendsTable;
